import {
  type UseMutationOptions,
  type UseMutationResult,
  useMutation,
} from "@tanstack/react-query";
import type { AxiosError, AxiosRequestConfig } from "axios";

import axiosInstance from "../../utils/axiosConfig";
import { compactParams } from "./params";

type HttpMethod = "post" | "put" | "patch" | "delete";

export type ApiMutationOptions<
  TData,
  TVariables,
  TContext = unknown
> = {
  method: HttpMethod;
  url: string | ((variables: TVariables) => string);
  /**
   * JSON body. Defaults to `variables` for post/put/patch when omitted.
   * For delete, omit or return `undefined` unless your API expects a body (`config.data`).
   */
  body?: (variables: TVariables) => unknown;
  params?: (
    variables: TVariables
  ) => Record<string, string | number | boolean | null | undefined> | undefined;
  axiosConfig?: (
    variables: TVariables
  ) => Pick<AxiosRequestConfig, "headers" | "timeout"> | undefined;
} & Omit<
  UseMutationOptions<TData, AxiosError, TVariables, TContext>,
  "mutationFn"
>;

function useApiMutationImpl<
  TData = unknown,
  TVariables = void,
  TContext = unknown
>(
  options: ApiMutationOptions<TData, TVariables, TContext>
): UseMutationResult<TData, AxiosError, TVariables, TContext> {
  const {
    method,
    url,
    body: bodyFn,
    params: paramsFn,
    axiosConfig: axiosCfgFn,
    ...mutationOptions
  } = options;

  return useMutation({
    ...mutationOptions,
    mutationFn: async (variables) => {
      const path = typeof url === "function" ? url(variables) : url;
      const extra = axiosCfgFn?.(variables);
      const rawParams = paramsFn?.(variables);
      const params = compactParams(
        rawParams as
          | Record<string, string | number | boolean | null | undefined>
          | undefined
      );

      let payload: unknown;
      if (method === "delete") {
        payload = bodyFn?.(variables);
      } else if (bodyFn) {
        payload = bodyFn(variables);
      } else {
        payload = variables as unknown;
      }

      switch (method) {
        case "post": {
          const { data } = await axiosInstance.post<TData>(path, payload, {
            params,
            ...extra,
          });
          return data;
        }
        case "put": {
          const { data } = await axiosInstance.put<TData>(path, payload, {
            params,
            ...extra,
          });
          return data;
        }
        case "patch": {
          const { data } = await axiosInstance.patch<TData>(path, payload, {
            params,
            ...extra,
          });
          return data;
        }
        case "delete": {
          const { data } = await axiosInstance.delete<TData>(path, {
            params,
            data: payload,
            ...extra,
          });
          return data;
        }
        default: {
          const _exhaustive: never = method;
          return _exhaustive;
        }
      }
    },
  });
}

/**
 * POST — body defaults to mutation variables when `body` is omitted.
 *
 * @example
 * const m = useApiPostMutation<Response, LoginPayload>("api/auth/login");
 * m.mutate({ email, password });
 */
export function useApiPostMutation<
  TData = unknown,
  TVariables = unknown,
  TContext = unknown
>(
  url: string | ((variables: TVariables) => string),
  options?: Omit<ApiMutationOptions<TData, TVariables, TContext>, "method" | "url">
): UseMutationResult<TData, AxiosError, TVariables, TContext> {
  return useApiMutationImpl({ method: "post", url, ...options });
}

/**
 * PATCH — body defaults to mutation variables when `body` is omitted.
 *
 * @example
 * const m = useApiPatchMutation<Response, { id: string; body: Partial<User> }>(
 *   (v) => `api/users/${v.id}`,
 *   { body: (v) => v.body }
 * );
 */
export function useApiPatchMutation<
  TData = unknown,
  TVariables = unknown,
  TContext = unknown
>(
  url: string | ((variables: TVariables) => string),
  options?: Omit<ApiMutationOptions<TData, TVariables, TContext>, "method" | "url">
): UseMutationResult<TData, AxiosError, TVariables, TContext> {
  return useApiMutationImpl({ method: "patch", url, ...options });
}

/**
 * PUT — body defaults to mutation variables when `body` is omitted.
 */
export function useApiPutMutation<
  TData = unknown,
  TVariables = unknown,
  TContext = unknown
>(
  url: string | ((variables: TVariables) => string),
  options?: Omit<ApiMutationOptions<TData, TVariables, TContext>, "method" | "url">
): UseMutationResult<TData, AxiosError, TVariables, TContext> {
  return useApiMutationImpl({ method: "put", url, ...options });
}

/**
 * DELETE — no body by default; pass `body` if the API expects JSON in `config.data`.
 *
 * @example
 * const m = useApiDeleteMutation<Response, string>(
 *   (id) => `api/users/${id}`
 * );
 * m.mutate(userId);
 */
export function useApiDeleteMutation<
  TData = unknown,
  TVariables = unknown,
  TContext = unknown
>(
  url: string | ((variables: TVariables) => string),
  options?: Omit<ApiMutationOptions<TData, TVariables, TContext>, "method" | "url">
): UseMutationResult<TData, AxiosError, TVariables, TContext> {
  return useApiMutationImpl({ method: "delete", url, ...options });
}

/**
 * Escape hatch: any method with full control (same shape as internal options).
 */
export function useApiMutation<
  TData = unknown,
  TVariables = void,
  TContext = unknown
>(
  options: ApiMutationOptions<TData, TVariables, TContext>
): UseMutationResult<TData, AxiosError, TVariables, TContext> {
  return useApiMutationImpl(options);
}
